'use client'

import { useEffect, useState } from 'react'
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react'

interface HealthCheck {
  ok: boolean
  ms?: number
  error?: string
}

interface HealthResponse {
  ok: boolean
  uptime_seconds: number
  checks: Record<string, HealthCheck>
  version: string
  timestamp: string
}

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; data: HealthResponse; lastChecked: Date }
  | { kind: 'error'; message: string; lastChecked: Date }

const REFRESH_MS = 30_000

function formatUptime(s: number): string {
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  if (days > 0) return `${days} Tage, ${hours} h`
  if (hours > 0) return `${hours} h ${minutes} min`
  return `${minutes} min`
}

const SERVICE_LABEL: Record<string, string> = {
  supabase: 'Datenbank (Supabase)',
}

function StatusBadge({ ok }: { ok: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
        ok ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'
      }`}>
      <span
        className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}
      />
      {ok ? 'Online' : 'Gestört'}
    </span>
  )
}

export function StatusBoard() {
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false

    async function check() {
      try {
        const r = await fetch('/api/health', { cache: 'no-store' })
        const data = (await r.json()) as HealthResponse
        if (!cancelled) {
          setState({ kind: 'ok', data, lastChecked: new Date() })
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Verbindung fehlgeschlagen',
            lastChecked: new Date(),
          })
        }
      }
    }

    check()
    const id = setInterval(check, REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  if (state.kind === 'loading') {
    return (
      <div className="mt-10 rounded-3xl border border-pub-rule bg-pub-surface p-8 flex items-center gap-3 text-pub-muted">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-[14px]">Live-Status wird abgerufen…</span>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="mt-10 rounded-3xl border border-red-300 bg-red-50 p-8 text-red-900">
        <div className="flex items-center gap-3">
          <XCircle className="h-6 w-6 text-red-600" />
          <p className="font-semibold text-[15px]">Statusseite nicht erreichbar</p>
        </div>
        <p className="mt-2 text-[13.5px] text-red-800/80">{state.message}</p>
        <p className="mt-3 text-[12px] text-red-700/60">
          Letzter Versuch: {state.lastChecked.toLocaleTimeString('de-DE')}
        </p>
      </div>
    )
  }

  const { data, lastChecked } = state
  const allOk = data.ok
  const checks = Object.entries(data.checks)

  return (
    <div className="mt-10 space-y-6">
      {/* Overall status banner */}
      <div
        className={`rounded-3xl border p-8 ${
          allOk
            ? 'border-emerald-200 bg-emerald-50/60'
            : 'border-amber-200 bg-amber-50/60'
        }`}>
        <div className="flex items-start gap-4">
          {allOk ? (
            <CheckCircle2 className="h-9 w-9 text-emerald-600 flex-shrink-0" />
          ) : (
            <AlertTriangle className="h-9 w-9 text-amber-600 flex-shrink-0" />
          )}
          <div>
            <p className="text-[20px] font-semibold text-pub-ink">
              {allOk ? 'Alle Systeme laufen normal' : 'Beeinträchtigung erkannt'}
            </p>
            <p className="mt-1 text-[13.5px] text-pub-muted">
              Uptime: {formatUptime(data.uptime_seconds)} · Version{' '}
              <code className="rounded bg-pub-accent-bg px-1.5 py-0.5 text-[12px] text-pub-accent">
                {data.version}
              </code>
            </p>
          </div>
        </div>
      </div>

      {/* Per-service status */}
      <div className="rounded-3xl border border-pub-rule bg-pub-surface overflow-hidden">
        <div className="px-6 py-4 border-b border-pub-rule">
          <p className="text-[12px] font-semibold uppercase tracking-widest text-pub-muted">
            Komponenten
          </p>
        </div>
        <ul className="divide-y divide-pub-rule">
          {checks.map(([service, check]) => (
            <li key={service} className="flex items-center justify-between px-6 py-4">
              <div>
                <p className="text-[14.5px] font-medium text-pub-ink">
                  {SERVICE_LABEL[service] ?? service}
                </p>
                {check.error && (
                  <p className="mt-1 text-[12px] text-red-600">{check.error}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {typeof check.ms === 'number' && (
                  <span className="text-[12px] text-pub-muted tabular-nums">
                    {check.ms} ms
                  </span>
                )}
                <StatusBadge ok={check.ok} />
              </div>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-center text-[11.5px] text-pub-muted">
        Letzte Prüfung: {lastChecked.toLocaleTimeString('de-DE')} · Aktualisiert sich alle{' '}
        {REFRESH_MS / 1000} Sekunden
      </p>
    </div>
  )
}
